import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { countySummary, type VoivodeshipOption } from "./counties";

/**
 * Powiaty pod zaznaczonym województwem — do doprecyzowania zasięgu.
 *
 * Lista jest domyślnie ZWINIĘTA: po zaznaczeniu województwa wszystkie powiaty są
 * objęte i w typowym przypadku nikt ich nie rusza. Rozwinięcie służy wyjątkowi —
 * firmie, która robi tylko kawałek województwa. Podsumowanie („3 z 20 powiatów")
 * widać bez rozwijania, żeby zawężenie nie ginęło z oczu.
 */
interface Props {
  option: VoivodeshipOption;
  /** Kody TERYT powiatów objętych zasięgiem (komplet = całe województwo). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Etykieta czynności rozwijającej — na karcie firmy „doprecyzuj", w ogłoszeniu „zawęź". */
  verb?: string;
  /** Wcięcie pod checkbox województwa (na karcie firmy); w osobnym polu formularza — bez. */
  indent?: boolean;
}

export function CountyRefinement({
  option,
  selected,
  onChange,
  verb = "doprecyzuj",
  indent = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const all = option.counties.map((c) => c.teryt);
  const summary = countySummary(selected.length, all.length);
  if (all.length === 0) return null;

  const toggle = (teryt: string) =>
    onChange(
      selected.includes(teryt) ? selected.filter((t) => t !== teryt) : [...selected, teryt],
    );

  return (
    <div className={indent ? "ml-6 mt-0.5" : "mt-0.5"}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex flex-wrap items-center gap-1 text-left text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
      >
        <ChevronRight
          size={12}
          className={`transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span className={selected.length === 0 ? "text-red-600 dark:text-red-400" : undefined}>
          {summary}
        </span>
        <span className="text-[var(--color-text-muted)]">· {open ? "zwiń" : verb}</span>
      </button>

      {open && (
        <div className="mt-1 border-l border-[var(--color-border)] pl-3">
          <div className="mb-1 flex gap-3 text-[11px] text-[var(--color-text-muted)]">
            <button
              type="button"
              className="hover:text-[var(--color-text-primary)]"
              onClick={() => onChange(all)}
            >
              zaznacz wszystkie
            </button>
            <button
              type="button"
              className="hover:text-[var(--color-text-primary)]"
              onClick={() => onChange([])}
            >
              odznacz wszystkie
            </button>
          </div>
          {/* `min-w-0` + zawijanie: komórka siatki nie rośnie pod długą nazwę, więc bez
              tego tekst wychodził poza nią i nakładał się na sąsiedni checkbox. */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 sm:grid-cols-3">
            {option.counties.map((county) => (
              <label
                key={county.teryt}
                className="flex min-w-0 items-start gap-1.5 text-xs leading-5"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(county.teryt)}
                  onChange={() => toggle(county.teryt)}
                  className="mt-1 shrink-0"
                />
                <span className="min-w-0 break-words text-[var(--color-text-primary)]">
                  {county.name}
                </span>
              </label>
            ))}
          </div>
          {selected.length === 0 && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              Wskaż co najmniej jeden powiat albo odznacz województwo.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Podgląd zawężenia (tryb odczytu): „3 z 20 powiatów" rozwijane do listy nazw.
 * Bez zawężenia nic nie rysuje — całe województwo mówi samo za siebie.
 */
export function CountyDisclosure({ names, total }: { names: string[]; total: number }) {
  const [open, setOpen] = useState(false);
  if (names.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs text-[var(--color-text-secondary)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-0.5 hover:text-[var(--color-text-primary)]"
      >
        <ChevronRight size={11} className={`transition-transform ${open ? "rotate-90" : ""}`} />
        {countySummary(names.length, total)}
      </button>
      {open && <span className="text-[var(--color-text-muted)]">{names.join(" · ")}</span>}
    </span>
  );
}
